import type { Ref } from 'vue';
import {
  GetDiscounts,
  type GetDiscountsQuery,
  type GetDiscountsQueryVariables,
} from '~/apollo/types/types';
import { REF_COOKIE_NAME } from '~/helpers/checkout';

/**
 * Composable that aggregates auto-applied coupon codes from multiple sources:
 * 1. Referral cookie (CakeCookie[ref])
 * 2. Strapi discounts (fetched via GetDiscounts GraphQL query)
 *
 * Returns deduplicated, case-insensitive (uppercased) reactive arrays
 * and the discount percentage for badge display.
 *
 * Does NOT push codes into the checkout store — callers are responsible
 * for syncing `couponCodes` into the store at the right time.
 */
export default function useCheckoutCoupons(
  planId: Ref<string | null>,
  productKey?: Ref<string | null>,
) {
  const route = useRoute();
  const refCookie = useCookie(REF_COOKIE_NAME);

  const refCouponCodes = computed<string[]>(() => {
    const queryRef = route.query.ref;
    const resultingCode =
      refCookie.value ||
      (queryRef
        ? String(Array.isArray(queryRef) ? queryRef[0] : queryRef)
        : null);

    if (resultingCode) {
      return resultingCode ? [`REFERRAL-${resultingCode.toUpperCase()}`] : [];
    }
    return [];
  });

  // Source 2: Strapi discounts
  const strapiCouponCodes = ref<string[]>([]);
  const discountPercent = ref<number | null>(null);

  async function fetchDiscounts(): Promise<void> {
    try {
      const { data } = await useLegacyApollo<
        GetDiscountsQuery,
        GetDiscountsQueryVariables
      >(GetDiscounts, {});

      if (!data?.discounts) return;

      const now = new Date();
      const match = data.discounts.find((d) => {
        const from = new Date(d.validFrom);
        const until = new Date(d.validUntil);
        if (now < from || now > until) return false;
        if (d.applyToAll) return true;
        const matchKey = productKey?.value?.trim() || planId.value;
        if (!matchKey) return false;
        return d.products?.some((p) => p.productKey === matchKey);
      });

      if (match) {
        discountPercent.value = match.percent;
        if (match.couponCode) {
          const code = match.couponCode.trim().toUpperCase();
          if (code) {
            strapiCouponCodes.value = [code];
          }
        }
      }
    } catch {
      // Discount lookup is non-critical
    }
  }

  // Combined, deduplicated coupon codes from all auto-apply sources
  const couponCodes = computed<string[]>(() => {
    const all = [...refCouponCodes.value, ...strapiCouponCodes.value];

    const seen = new Set<string>();
    const unique: string[] = [];

    for (const code of all) {
      const normalized = code.trim().toUpperCase();
      if (normalized && !seen.has(normalized)) {
        seen.add(normalized);
        unique.push(normalized);
      }
    }

    return unique;
  });

  return {
    /** All auto-applied coupon codes (reflink + Strapi discounts), deduplicated. */
    couponCodes,
    /** Discount percentage from the matching Strapi discount, for badge display. */
    discountPercent,
    /** Fetch Strapi discounts and populate strapiCouponCodes + discountPercent. */
    fetchDiscounts,
  };
}
